import { Injectable } from '@angular/core';
import { LoggerService } from '../../logger/logger.service';
import { SessionService } from '../../session/session.service';
import { environment } from '../../../../environments/environment';

export interface ChatAttributes {
    PolicyNumber?: string;
    AgencyName?: string;
    BillingAccount?: string;
    InteractionSource?: string;
    ChatEmailAddress?: string;
    FirstName?: string;
    LastName?: string;
    PageTitle?: string;
    BusinessUnit?: string;
    InteractionSourceSystem?: string;
}

@Injectable({ providedIn: 'root' })
export class ClickToChatService {
    private isReady = false;
    private pendingClearConversation = false;

    constructor(
        private loggerService: LoggerService,
        private sessionService: SessionService
    ) { }

    clearConversation(): void {
        if (this.isReady) {
            this.executeClearConversation();
        } else {
            this.loggerService.info('Chat - Not ready yet, will clear when ready');
            this.pendingClearConversation = true;
        }
    }

    initializeChat(): void {
        if (window['Genesys']) {
            window['Genesys']('subscribe', 'Database.ready', () => {

                window['Genesys']('subscribe', 'Launcher.ready', () => {
                    this.isReady = true;
                    this.loggerService.info('Chat - Chat is ready');

                    if (this.pendingClearConversation) {
                        this.pendingClearConversation = false;
                        this.executeClearConversation();
                    } else {
                        window['Genesys']('subscribe', 'Messenger.opened', () => {
                            this.loggerService.info('Chat - Chat was opened');
                            this.timeoutForChat();
                        });
                    }
                });
            });

        }
    }

    checkToPreserveChatOnLoad(): void {
        let referrerHost = '';
        try {
            if (document.referrer) {
                referrerHost = new URL(document.referrer).hostname;
            }
        } catch (error) {
            this.loggerService.info('Chat could not parse referrer', error);
        }

        const cameFromKnownApp = referrerHost.includes(environment.appURLs.isb) || referrerHost.includes(environment.appURLs.isp);

        if (cameFromKnownApp) {
            this.loggerService.info('Chat persistence allowed on load — preserving Genesys session');
        } else {
            this.loggerService.info('Chat persistence disallowed on load — clearing Genesys session');
            this.clearConversation();
        }
    }

    private createTimeout(duration: number): any {
        return setTimeout(() => {
            window['Genesys']('command', 'MessagingService.clearConversation', {}, () => {
                this.loggerService.info('Chat - Cleared chat on timeout');
            },
                () => {
                    this.loggerService.info('Chat - Failed to clear chat on timeout');
                }
            );
        }, duration);
    }

    private executeClearConversation(): void {
        window['Genesys']('command', 'Messenger.close', {}, () => { }, () => { });
        window['Genesys']('command', 'MessagingService.clearConversation', {}, () => {
            this.loggerService.info('Chat - Cleared chat on load');
        },
            () => {
                this.loggerService.info('Chat - Failed to clear chat on load');
            }
        );
    }

    private timeoutForChat(): void {
        const TIMEOUT_DURATION = 300000; // 5 minutes in milliseconds

        let sendMessageTimeout: any = null;
        let timerReset = false; // Flag to track if the timer has already been reset after the first outbound message

        // Event listener for receiving messages
        window['Genesys']('subscribe', 'MessagingService.messagesReceived', ({ data }: { data: any }) => {
            // Check if the data contains any 'outbound' messages
            if (data?.messages?.some((message: any) => message.direction?.toLowerCase() === 'outbound')) {
                this.loggerService.info('Chat - Member received a message');
                // Reset the timer only once on the first outbound message
                if (!timerReset) {
                    if (sendMessageTimeout) {
                        clearTimeout(sendMessageTimeout);
                    }
                    // Set the timeout to alert after the configured duration if no message is sent
                    sendMessageTimeout = this.createTimeout(TIMEOUT_DURATION);
                    // Set the flag to true so the timer won't reset again for subsequent outbound messages
                    timerReset = true;
                }
            }
        });

        // Event listener for sending messages
        window['Genesys']('subscribe', 'MessagingService.sendingMessage', ({ data }: { data: any }) => {
            this.loggerService.info('Chat - Member sent a message');
            // If a message is sent, clear the timeout and reset it
            if (sendMessageTimeout) {
                clearTimeout(sendMessageTimeout);
            }
            // Reset the timeout since a message was sent
            sendMessageTimeout = this.createTimeout(TIMEOUT_DURATION);
        });
    }

}
